import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { orgCardView } from '../../../layout/routes';
import { resolvePath, welcome } from '../../../routes';
import { fromAccounts, fromAccountUsers, fromAuth, useAppDispatch } from '../../../store';
import { useLaunchDarkly } from '../../../launchdarkly';

enum UserType {
  GEN2 = 'GEN2',
  GEN3 = 'GEN3',
  MIXED = 'MIXED',
}

const AccountWelcome = () => {
  const { initialized, flags } = useLaunchDarkly();

  const [loadingAccountUser, setLoadingAccountUser] = useState(true);
  const [totalAccountGen3, setTotalAccountGen3] = useState<number>();

  const user = useSelector(fromAuth.selectUser);
  const accountUser = useSelector(
    fromAccountUsers.selectMinimumAccountIdAccountUsersByUserId(user!!.id),
  );
  const totalAccount = useSelector(fromAccountUsers.selectCountByUserId(user!!.id));

  const isLoading = useMemo(
    () => (loadingAccountUser || totalAccountGen3 === undefined || initialized !== true),
    [loadingAccountUser, totalAccountGen3, initialized],
  );

  const userType = useMemo(() => {
    if (isLoading) {
      return undefined;
    }
    const safeAccountGen3 = totalAccountGen3 ?? 0;
    if (totalAccount === safeAccountGen3) {
      return UserType.GEN3;
    }
    if (safeAccountGen3 > 0) {
      return UserType.MIXED;
    }
    return UserType.GEN2;
  }, [isLoading, totalAccount, totalAccountGen3]);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!user) return;
    dispatch(
      fromAccountUsers.doGetAccountUsersByUserId({ userId: user!!.id }),
    ).then(() => setLoadingAccountUser(false));
    dispatch(fromAccounts.doCountAccountGen3())
      .then(unwrapResult)
      .then(numOfGen3Account => {
        setTotalAccountGen3(numOfGen3Account);
      });
  }, [JSON.stringify(user)]);

  if (isLoading || userType === undefined) {
    return null;
  }

  if (flags?.setAccountGen3OnCreated !== true) {
    return (
      <Navigate to={
        resolvePath(orgCardView, undefined, { accountId: accountUser.accountId }, false)
        }
      />
    );
  }

  switch (userType) {
    // gen2 only
    case UserType.GEN2:
      return (
        <Navigate to={
          resolvePath(orgCardView, undefined, { accountId: accountUser.accountId }, false)
          }
        />
      );
    // gen3 only
    case UserType.GEN3:
      window.location.replace(`https://platform.${process.env.REACT_APP_CUSTOM_DOMAIN_POSTFIX}`);
      return null;
    // mixed
    default:
      return (
        <Navigate to={resolvePath(welcome, undefined, {}, false)} />
      );
  }
};

export default AccountWelcome;
